import { ReactNode } from "react";

interface ButtonProps {
  children: ReactNode; // Button text or content
  size?: "sm" | "md"; // Button size
  variant?: "primary" | "outline"; // Button variant
  // Native button type. Defaults to "button" so a Button placed inside a <form>
  // never submits by accident (e.g. a Cancel button). Set type="submit"
  // explicitly on the button that should submit the form.
  type?: "button" | "submit";
  startIcon?: ReactNode; // Icon before the text
  endIcon?: ReactNode; // Icon after the text
  onClick?: () => void; // Click handler
  disabled?: boolean; // Disabled state
  className?: string; // Disabled state
}

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  type = "button",
  startIcon,
  endIcon,
  onClick,
  className = "",
  disabled = false,
}) => {
  // Size Classes
  const sizeClasses = {
    sm: "px-4 py-3 text-sm",
    md: "px-5 py-3.5 text-sm",
  };

  // Variant Classes
  //
  // The primary action is near-black, not forest. Colour in this app carries
  // MEANING — forest is the frame, yolk is "look here", and success/error/
  // warning say what a figure is. A forest button competes with the forest
  // sidebar for the same attention and adds a fifth green to the screen
  // without saying anything the shape of a button did not already say.
  //
  // gray-900 (#16221d) is near-black carrying the brand's own hue, so this
  // is a neutral that still belongs to the palette rather than a stock black
  // dropped on top of it.
  //
  // Dark mode inverts rather than lightening: near-black on a near-black
  // page is not a button.
  const variantClasses = {
    primary:
      "bg-gray-900 text-white shadow-theme-xs hover:bg-gray-800 disabled:bg-gray-400 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:disabled:bg-gray-600",
    outline:
      "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300",
  };

  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-2 rounded-lg transition ${className} ${
        sizeClasses[size]
      } ${variantClasses[variant]} ${
        disabled ? "cursor-not-allowed opacity-50" : ""
      }`}
      onClick={onClick}
      disabled={disabled}
    >
      {startIcon && <span className="flex items-center">{startIcon}</span>}
      {children}
      {endIcon && <span className="flex items-center">{endIcon}</span>}
    </button>
  );
};

export default Button;
